import { useEffect, useState } from "react";
import { Star, Loader2, ShieldCheck, Quote } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MobileScreenHeader from "../components/MobileScreenHeader";

/** Received reviews for the current user (as a partner target). */
export default function MyReviews() {
    const { user } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        if (!user?.id) return;
        setLoading(true);
        try {
            const { data } = await api.get("/reviews", { params: { target_id: user.id, limit: 100 } });
            setReviews(data || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [user?.id]);

    const avg = reviews.length
        ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
        : "0.0";
    const verifiedCount = reviews.filter((r) => r.verified).length;

    return (
        <div className="min-h-screen bg-[#f6f7fb]" data-testid="my-reviews-page">
            <MobileScreenHeader title="My Reviews" subtitle="Ratings received" testid="reviews-header" />

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#002657]/60">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <>
                    {/* Rating summary */}
                    <div className="px-4 -mt-2">
                        <div
                            className="text-on-image rounded-2xl p-5 shadow-xl"
                            style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 60%, #003366 100%)", boxShadow: "0 15px 35px -12px rgba(0,15,35,0.55)" }}
                            data-testid="reviews-summary-card"
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#F5E5A5" }}>Overall Rating</div>
                                <Star className="w-5 h-5 fill-current" style={{ color: "#F5E5A5" }} />
                            </div>
                            <div className="mt-2 flex items-baseline gap-3">
                                <div className="font-heading font-extrabold text-white text-4xl" data-testid="reviews-avg">{avg}</div>
                                <div className="text-white/70 text-[13px]">/ 5.0</div>
                                <div className="ml-auto text-right">
                                    <div className="text-white font-bold text-[13px]" data-testid="reviews-count">{reviews.length} reviews</div>
                                    <div className="text-[10px]" style={{ color: "#F5E5A5" }}>
                                        <ShieldCheck className="w-3 h-3 inline mr-0.5" /> {verifiedCount} verified
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Reviews list */}
                    <div className="mt-5 px-4 pb-6">
                        <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712] mb-2">All Reviews</div>
                        {reviews.length === 0 ? (
                            <div className="rounded-2xl bg-white border border-[#002657]/10 p-8 text-center" data-testid="reviews-empty">
                                <Quote className="w-8 h-8 text-[#8B6712]/40 mx-auto mb-2" />
                                <div className="text-[13px] font-bold text-[#002657]">No reviews yet</div>
                                <div className="text-[11px] text-[#002657]/55 mt-1">Once buyers rate you, their feedback shows here.</div>
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="reviews-list">
                                {reviews.map((r) => (
                                    <div key={r.id} className="rounded-2xl bg-white border border-[#002657]/10 p-3.5 shadow-sm" data-testid={`review-item-${r.id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="text-[13px] font-bold text-[#002657]">{r.reviewer_name || "Anonymous"}</div>
                                                <div className="text-[10px] text-[#002657]/55">{(r.created_at || "").slice(0, 10)}</div>
                                            </div>
                                            <div className="flex items-center gap-0.5 shrink-0" style={{ color: "#DAA520" }}>
                                                {[1,2,3,4,5].map((n) => (
                                                    <Star key={n} className={`w-3.5 h-3.5 ${n <= (r.rating || 0) ? "fill-current" : ""}`} />
                                                ))}
                                            </div>
                                        </div>
                                        {r.comment && (
                                            <p className="mt-2 text-[12px] leading-relaxed text-[#002657]/85 line-clamp-6">&ldquo;{r.comment}&rdquo;</p>
                                        )}
                                        {r.verified && (
                                            <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-extrabold uppercase tracking-widest">
                                                <ShieldCheck className="w-2.5 h-2.5" /> Verified Review
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
